import React, { useRef } from 'react';
import { m, useInView } from 'framer-motion';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material';

import { CONFIG } from 'src/global-config';
import { Image } from 'src/components/image';
import { useLocale } from 'src/locales';

// ----------------------------------------------------------------------

export function AboutCommitment({ sx, ...other }) {
  const { t } = useLocale();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <Box
      ref={ref}
      component="section"
      sx={[
        {
          overflow: 'hidden',
          py: { xs: 5, md: 4 },
          textAlign: { xs: 'center', md: 'unset' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <Box>
          <Typography
            variant="h2"
            sx={{ mb: 5, textAlign: 'center', mt: { xs: 3, md: 0 }, fontSize: '2rem !important' }}
          >
            {t('Our_Commitment_to_Trust_and_Compliance')}
          </Typography>
          <Typography
            sx={{ color: 'text.secondary', maxWidth: 720, mx: 'auto', mb: 8, textAlign: 'center' }}
          >
            {t('We_believe_that_trust_is_the_foundation_of_our_partnerships')}
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{ mb: 5, textAlign: 'center', mt: { xs: 3, md: 0 }, fontSize: '2rem !important' }}
        >
          {t('Pillars_of_Our_Commitment')}
        </Typography>
        <Grid
          container
          spacing={{ xs: 5, md: 3 }}
          sx={{ justifyContent: 'center', gap: { xs: 5, md: 10 } }}
          alignItems={'center'}
        >
          <Grid size={{ xs: 12, md: 3 }}>
            <m.div
              initial={{ opacity: 0, y: -80 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Typography variant="h5" sx={{}}>
                {t('Full_Regulatory_Compliance')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 3 }}>
                {t('We_operate_under_an_official_license_from_the_Transport_General_Authority')}
              </Typography>
            </m.div>
            <m.div
              initial={{ opacity: 0, y: -80 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Typography variant="h5" sx={{ mt: 3 }}>
                {t('Absolute_Operational_Transparency')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 3 }}>
                {t('We_provide_clients_with_real_time_dashboards_and_detailed_reports')}
              </Typography>
            </m.div>
          </Grid>

          <Grid size={{ xs: 12, md: 4.5 }}>
            <m.div
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Image
                alt={t('Leading_land_transport_companies_in_Saudi_Arabia_licensed_by_TGA')}
                src={`${CONFIG.assetsDir}/assets/images/home/about/feker-communications-it-shahen-owner.webp`}
                ratio="3/4"
                sx={{
                  display: 'block',
                  width: '100%',
                  height: 480,
                  borderRadius: 2,
                  maxWidth: { xs: 300, sm: 400, md: 480 },
                  mx: 'auto',
                  maxHeight: { xs: 200, sm: 300, md: 480 },
                  objectFit: 'cover',
                }}
                priority
              />
            </m.div>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <m.div
              initial={{ opacity: 0, y: -80 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Typography variant="h5" sx={{}}>
                {t('Protection_and_Security')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 3 }}>
                {t(
                  'Every_shipment_is_fully_insured_to_protect_client_interests_against_operational_risks'
                )}
              </Typography>
            </m.div>
            <m.div
              initial={{ opacity: 0, y: -80 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Typography variant="h5" sx={{ mt: 3 }}>
                {t('Financial_Integrity_and_Data_Protection')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 3 }}>
                {t('We_uphold_the_highest_standards_of_financial_transparency')}
              </Typography>
            </m.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
